// public/js/features/assets/trip-minimap.js
// The route of ONE journey, rendered in an expandable panel on the asset page.
//
// Two deliberate choices:
//  - The map is ROTATED so the journey runs left to right. A trip is read like
//    a sentence; north-up wastes most of a wide, short panel on empty space.
//    A button switches back to north for anyone who needs orientation.
//  - Stretches where the tracker went quiet are drawn DASHED. The straight line
//    across them is an interpolation, not a surveyed route, and the map should
//    not claim otherwise (see `interpolatedM` on the journey).
import { DEFAULT_MAPLIBRE_STYLE } from '../../modules/map/constants.js';

// A step longer than this between consecutive fixes is treated as a reporting
// gap rather than a travelled leg.
const GAP_M = 1500;

const EARTH_R = 6371000;
const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

function metres(aLat, aLon, bLat, bLon) {
  const dLat = rad(bLat - aLat), dLon = rad(bLon - aLon);
  const h = Math.sin(dLat / 2) ** 2 +
            Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Screen angle (degrees) of the A→B chord in the MAP PROJECTION (Web Mercator),
 * on a north-up map: clockwise from screen-up. This is what must be aligned to
 * the horizontal — NOT the geographic great-circle bearing, which diverges from
 * the on-screen direction over a long leg (the great circle curves on Mercator),
 * tilting long trips like Delhi→Frankfurt. Projecting both endpoints removes the
 * distance/latitude dependence entirely.
 */
function projectedAngle(a, b) {
  const A = window.maplibregl.MercatorCoordinate.fromLngLat([a.lon, a.lat]);
  const B = window.maplibregl.MercatorCoordinate.fromLngLat([b.lon, b.lat]);
  // Mercator y increases southward (screen-down); up is -y.
  return deg(Math.atan2(B.x - A.x, -(B.y - A.y)));
}

/** Split a point list into solid travelled runs and dashed reporting gaps. */
function buildLines(points) {
  const travelled = [], gaps = [];
  let run = [[points[0].lon, points[0].lat]];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1], b = points[i];
    const d = metres(a.lat, a.lon, b.lat, b.lon);
    if (d > GAP_M) {
      if (run.length > 1) travelled.push(run);
      gaps.push([[a.lon, a.lat], [b.lon, b.lat]]);
      run = [[b.lon, b.lat]];
    } else {
      run.push([b.lon, b.lat]);
    }
  }
  if (run.length > 1) travelled.push(run);
  return { travelled, gaps };
}

const fc = (lines) => ({
  type: 'FeatureCollection',
  features: lines.map(coords => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } })),
});

const MAX_ZOOM = 17;
const FIT_PADDING = 22; // px kept clear on every edge

/**
 * Tightly frame a rotated route. fitBounds only fits the axis-aligned lng/lat
 * box; once the camera is rotated that box's on-screen footprint is far larger
 * than the route, so it zooms way out. Instead: project every point, rotate into
 * the trip-aligned frame, measure the route's real bounding box there, and set
 * zoom + centre so it fills the panel (down to a minimal margin). Same maths as
 * MapLibre's own projection (512-px tiles, mercator 0..1), so it lands exactly.
 */
function fitRotatedRoute(map, points, bearingDeg) {
  const beta = rad(bearingDeg);
  const cos = Math.cos(beta), sin = Math.sin(beta);
  const ref = window.maplibregl.MercatorCoordinate.fromLngLat([points[0].lon, points[0].lat]);

  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (const p of points) {
    const m = window.maplibregl.MercatorCoordinate.fromLngLat([p.lon, p.lat]);
    const dx = m.x - ref.x, dy = m.y - ref.y;
    const sx = dx * cos + dy * sin;    // along-trip (horizontal after rotation)
    const sy = -dx * sin + dy * cos;   // cross-trip
    if (sx < minx) minx = sx; if (sx > maxx) maxx = sx;
    if (sy < miny) miny = sy; if (sy > maxy) maxy = sy;
  }

  const spanX = Math.max(maxx - minx, 1e-12);
  const spanY = Math.max(maxy - miny, 1e-12);
  const el = map.getContainer();
  const availW = Math.max(1, (el.clientWidth  || 600) - 2 * FIT_PADDING);
  const availH = Math.max(1, (el.clientHeight || 260) - 2 * FIT_PADDING);
  const TILE = 512;
  const scale = Math.min(availW / (spanX * TILE), availH / (spanY * TILE));
  const zoom = Math.min(MAX_ZOOM, Math.log2(scale));

  // Centre = the rotated box's midpoint, rotated back into mercator.
  const csx = (minx + maxx) / 2, csy = (miny + maxy) / 2;
  const cmx = ref.x + (csx * cos - csy * sin);
  const cmy = ref.y + (csx * sin + csy * cos);
  const center = new window.maplibregl.MercatorCoordinate(cmx, cmy).toLngLat();

  map.jumpTo({ center, zoom, bearing: bearingDeg });
}

/**
 * Draw a journey's route into `container`.
 * @param {HTMLElement} container
 * @param {string} assetUUID
 * @param {{startTime,endTime}} journey
 */
export async function renderTripMinimap(container, assetUUID, journey) {
  if (!window.maplibregl) { container.innerHTML = '<div class="text-muted small">Map library unavailable.</div>'; return; }

  container.innerHTML = `
    <div class="position-relative rounded overflow-hidden border" style="height:260px">
      <div class="trip-map w-100 h-100"></div>
      <button type="button" class="btn btn-sm btn-light position-absolute shadow-sm trip-map-north"
              style="top:8px; right:8px; z-index:2" title="Rotate to north">
        <i class="fa-solid fa-location-arrow"></i> N
      </button>
      <div class="position-absolute text-muted small px-2 py-1 bg-white bg-opacity-75 rounded trip-map-note"
           style="bottom:8px; left:8px; z-index:2"></div>
    </div>`;

  const host = container.querySelector('.trip-map');
  const note = container.querySelector('.trip-map-note');
  note.textContent = 'Loading route…';

  let points = [];
  try {
    const qs = new URLSearchParams({ since: journey.startTime, until: journey.endTime });
    const res = await window.authenticatedFetch(
      `api/v1/assets/${encodeURIComponent(assetUUID)}/history?${qs}`);
    if (!res.ok) throw new Error('history');
    points = (await res.json()).history || [];
  } catch {
    note.textContent = 'Route could not be loaded.';
    return;
  }

  points = points
    .filter(p => p.lat != null && p.lon != null)
    .map(p => ({ lat: Number(p.lat), lon: Number(p.lon) }));

  if (points.length < 2) { note.textContent = 'No route recorded for this trip.'; return; }

  const { travelled, gaps } = buildLines(points);
  const a = points[0], b = points[points.length - 1];

  const map = new window.maplibregl.Map({
    container: host,
    style: DEFAULT_MAPLIBRE_STYLE,
    attributionControl: false,
    interactive: true,
  });

  map.on('load', () => {
    map.addSource('trip-line', { type: 'geojson', data: fc(travelled) });
    map.addLayer({
      id: 'trip-line', type: 'line', source: 'trip-line',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#3b82f6', 'line-width': 4 },
    });

    if (gaps.length) {
      map.addSource('trip-gaps', { type: 'geojson', data: fc(gaps) });
      map.addLayer({
        id: 'trip-gaps', type: 'line', source: 'trip-gaps',
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': '#3b82f6', 'line-width': 3, 'line-dasharray': [2, 2], 'line-opacity': 0.7 },
      });
    }

    new window.maplibregl.Marker({ color: '#16a34a' }).setLngLat([a.lon, a.lat]).addTo(map);
    new window.maplibregl.Marker({ color: '#dc2626' }).setLngLat([b.lon, b.lat]).addTo(map);

    // Rotate so the journey reads left → right.
    //
    // The bearing MUST be passed to fitBounds. It does not preserve the current
    // camera rotation — with no `bearing` option it frames the bounds north-up
    // and silently undoes a preceding setBearing(), which is why the map opened
    // unrotated and only snapped into place on the first button press.
    //
    // The angle is the chord's angle IN THE PROJECTION, so the horizontal holds
    // for long, high-latitude legs too (a great-circle bearing would not). A
    // north-up screen already has the chord 90° clockwise from up, so subtract 90.
    const alongTrip = projectedAngle(a, b) - 90;

    // Rotate AND zoom in one shot: a rotation-aware tight fit fills the panel,
    // where fitBounds would zoom too far out for a rotated route (see above).
    fitRotatedRoute(map, points, alongTrip);

    note.textContent = gaps.length
      ? `${points.length} points · dashed = ${gaps.length} interpolated gap${gaps.length === 1 ? '' : 's'}`
      : `${points.length} points`;

    // The map starts aligned with the trip, so the button's job is to give the
    // reader north back — and then to hand the trip view back again.
    const btn = container.querySelector('.trip-map-north');
    const paint = () => {
      const isNorth = Math.abs(map.getBearing()) < 1;
      btn.title = isNorth ? 'Align with the trip' : 'Rotate to north';
      btn.classList.toggle('active', isNorth);
    };
    paint();
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isNorth = Math.abs(map.getBearing()) < 1;
      map.easeTo({ bearing: isNorth ? alongTrip : 0, duration: 400 });
    });
    map.on('rotateend', paint);
  });

  // The panel is hidden until expanded, so the canvas is sized wrong on first
  // paint unless it is told to re-measure.
  setTimeout(() => { try { map.resize(); } catch {} }, 60);
  return map;
}
